import type { GetRequestsListResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import apiClient from "@/lib/api/client";

import type { DesignerFilterType } from "../../components/DesignerDropdownFilter";

type DesignerSectionType =
  | "awaiting-reply"
  | "being-revised"
  | "in-progress"
  | "others"
  | "ready-for-review";
type DesignerTabType = "assigned-to-any" | "assigned-to-me";
type ViewModeType = "section" | "single-table";

interface UseDesignerRequestDataParams {
  /**
   * Pagination and sorting options
   */
  direction?: "asc" | "desc";
  enabled?: boolean;
  /**
   * The filter applied from the dropdown (all, completed, open, overdue, due-today)
   */
  filter: DesignerFilterType;
  limit?: number;
  page?: number;
  search?: string;
  /**
   * The section to display (only used in "section" viewMode)
   */
  section?: DesignerSectionType;
  skipCount?: boolean;
  sortBy?: string;
  /**
   * The Designer tab being displayed
   * - "assigned-to-me": Requests assigned to the current designer
   * - "assigned-to-any": All requests assigned to any designer
   */
  tab: DesignerTabType;
  /**
   * Optional user ID override. When provided, fetches requests for this user
   * instead of the logged-in user. Used when viewing another user's profile.
   */
  userId?: string;
  /**
   * The view mode determines whether to use sections or single-table
   * - "section": Multi-section mode (In Progress, Being Revised, Awaiting Reply, Ready for Review, Others)
   * - "single-table": Single-table mode (completed, due-today, overdue)
   */
  viewMode: ViewModeType;
}

/**
 * Unified hook for fetching Designer request data
 * Consolidates all Designer data fetching into a single configurable hook
 *
 * @example
 * // Assigned to Me - Section Mode
 * useDesignerRequestData({
 *   tab: "assigned-to-me",
 *   viewMode: "section",
 *   section: "in-progress",
 *   filter: "all"
 * })
 *
 * @example
 * // Assigned to Any - Single Table Mode
 * useDesignerRequestData({
 *   tab: "assigned-to-any",
 *   viewMode: "single-table",
 *   filter: "completed"
 * })
 */
export function useDesignerRequestData({
  direction = "desc",
  enabled = true,
  filter,
  limit = 10,
  page = 1,
  search = "",
  section,
  skipCount,
  sortBy = "createdAt",
  tab,
  userId,
  viewMode,
}: UseDesignerRequestDataParams) {
  return useQuery({
    enabled,
    queryFn: async () => {
      const params: Record<string, string> = {
        designerFilter: filter,
        direction,
        limit: limit.toString(),
        page: page.toString(),
        sortBy,
        tab,
      };

      // When viewing another user's requests, pass forUserId to override context
      if (userId) {
        params.forUserId = userId;
      }

      // Only include designerSection for section-based views
      if (viewMode === "section" && section) {
        params.designerSection = section;
      }

      // Include search if provided
      if (search) {
        params.search = search;
      }

      const shouldSkipCount = skipCount ?? page > 1;

      params.skipCount = shouldSkipCount.toString();

      const queryString = new URLSearchParams(params).toString();

      return await apiClient
        .url(`/requests?${queryString}`)
        .get()
        .json<GetRequestsListResponse>();
    },
    queryKey: [
      "designerRequests",
      tab,
      viewMode,
      ...(viewMode === "section" && section ? [section] : ["single-table"]),
      filter,
      page,
      limit,
      sortBy,
      direction,
      search,
      skipCount ?? page > 1,
      ...(userId ? [userId] : []),
    ],
    staleTime: 30_000, // 30 seconds
  });
}
